import { Grid, IconButton, InputAdornment } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { rem } from 'src/common/helpers/dimension-helper';
import { getDefaultWeightUomText } from 'src/common/helpers/localized-description-helper';
import { truncateNumberWithPrecision } from 'src/common/helpers/number-helper';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { useTranslation } from 'react-i18next';
import { CssTextField } from 'src/css-text-field/css-text-field';
import { themeColor } from 'src/mui-theme';
import { styles } from '../tare-weight-items-styles';

const TareWeightApproval = (
  {
    setTareValue,
    setIsPercentage,
    setItemWeight,
    grossWeight,
    linkedGroupItems,
    isManualTarePercentageEnabled,
    isTareModification,
    handleCancel,
    handleApprove,
  }: TareWeightApprovalProps,
  ref,
): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const [fixedValue, setFixedValue] = useState('');
  const [percentageValue, setPercentageValue] = useState('');

  useImperativeHandle(ref, () => ({
    clearValues: () => {
      clearFixedValue();
      clearPercentageValue();
    },
  }));

  const handleChange = (event): void => {
    const {
      target: { name, value },
    } = event;
    const valueWithPrecision = truncateNumberWithPrecision(value);
    if (name === 'tarevalue') {
      setIsPercentage(false);
      setFixedValue(valueWithPrecision);
      setPercentageValue('');
      setTareValue(valueWithPrecision);
      setItemWeight(valueWithPrecision);
    } else if (name === 'percentage') {
      setIsPercentage(true);
      setPercentageValue(valueWithPrecision);
      setFixedValue('');
      if (grossWeight) {
        const itemWeight = String((grossWeight * 0.01 * Number(valueWithPrecision)).toFixed(2));
        setItemWeight(itemWeight);
      }
      setTareValue(valueWithPrecision);
    }
  };

  const clearFixedValue = (): void => {
    setFixedValue('');
    setTareValue('');
  };

  const clearPercentageValue = (): void => {
    setPercentageValue('');
    setTareValue('');
  };
  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='center'
      className={
        linkedGroupItems && !isTareModification
          ? classes.parentInputContainer
          : classes.noLinkedTareContainer
      }
      data-testid='tare-weight-approval'
    >
      <Grid
        item
        container
        justifyContent='center'
        alignItems='center'
        className={
          linkedGroupItems && !isTareModification
            ? classes.parentManualTareinputContainer
            : classes.noLinkedTareManualTareinputContainer
        }
      >
        <Grid item container>
          <Grid item className={'fullWidth'}>
            <Grid item>
              <CssTextField
                classes={{
                  root: classes.underline,
                }}
                className={`${classes.inputTextField}`}
                variant='standard'
                inputProps={{
                  'data-testid': 'tare-weight-items-fixed-input',
                  'data-keyboard-owner': 'tare-weight-items-focus',
                }}
                InputProps={{
                  endAdornment: (
                    <InputCancelAdornment
                      name='cancelIcon'
                      input={fixedValue}
                      cancelIconSize='small'
                      clearInput={clearFixedValue}
                    />
                  ),
                }}
                id='tarevalue'
                label={t('ManualTareWeight_EnterWeight')}
                value={fixedValue}
                name='tarevalue'
                autoComplete='off'
                autoFocus={true}
                onChange={handleChange}
              />
              <div className={`${classes.inputTextFieldUnderline}`}>
                {getDefaultWeightUomText()}
              </div>
            </Grid>
          </Grid>
        </Grid>
        {isManualTarePercentageEnabled && (
          <Grid item container className={`${classes.inputContainerPercentage}`}>
            <Grid item className={'fullWidth'}>
              <CssTextField
                classes={{
                  root: classes.underline,
                }}
                className={`${classes.inputTextField}`}
                variant='standard'
                id='tareValuePercent'
                name='percentage'
                autoComplete='off'
                label={t('ManualTareWeight_EnterPercentage')}
                inputProps={{
                  'data-testid': 'tare-weight-items-percentage-input',
                  'data-keyboard-owner': 'tare-weight-items-focus',
                }}
                InputProps={{
                  endAdornment: (
                    <InputCancelAdornment
                      name='cancelIcon'
                      input={percentageValue}
                      cancelIconSize='small'
                      clearInput={clearPercentageValue}
                    />
                  ),
                }}
                value={percentageValue}
                onChange={handleChange}
              />
              <div className={`${classes.inputTextFieldUnderline}`}>%</div>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid
        item
        container
        justifyContent='center'
        className={`${classes.buttonClass} ${classes.marginSeparator}`}
      >
        <BaseButton
          buttonType='white'
          className={classes.cancelButton}
          onClick={handleCancel}
          data-testid='tare-weight-approval-cancel-button'
        >
          {t('Cancel')}
        </BaseButton>
        <BaseButton
          className={classes.approveButton}
          buttonType='primary'
          onClick={handleApprove}
          data-testid='tare-weight-approval-button'
          disabled={fixedValue.trim().length === 0 && percentageValue.trim().length === 0}
        >
          {t('Approve')}
        </BaseButton>
      </Grid>
    </Grid>
  );
};

interface IInputCancelAdornmentProps {
  name: string;
  input: string;
  cancelIconSize?: 'small' | 'large';
  clearInput: () => void;
}

const InputCancelAdornment = ({
  name,
  input,
  cancelIconSize = 'large',
  clearInput,
}: IInputCancelAdornmentProps): JSX.Element => {
  return (
    <InputAdornment position='end'>
      <IconButton
        style={{ padding: cancelIconSize === 'small' ? 0 : 12 }}
        data-testid={`clear-button-${name}`}
        aria-label={`clear ${name} input`}
        onClick={(e): void => {
          clearInput();
          e.preventDefault();
        }}
      >
        {input ? (
          <ClearIcon
            style={{ fontSize: cancelIconSize === 'small' ? rem(1) : rem(1.5) }}
            htmlColor={`${themeColor.infoMain}`}
          />
        ) : (
          ''
        )}
      </IconButton>
    </InputAdornment>
  );
};
interface TareWeightApprovalProps {
  handleApprove: () => void;
  handleCancel: () => void;
  setTareValue: (data: any) => void;
  grossWeight?: number;
  setIsPercentage: (flag: boolean) => void;
  setItemWeight: (data: string) => void;
  isManualTarePercentageEnabled: boolean;
  isTareModification: boolean;
  linkedGroupItems?: IItemLookup[];
}

export type TareWeightApprovalHandle = {
  clearValues: () => void;
};

export default forwardRef<TareWeightApprovalHandle, TareWeightApprovalProps>(TareWeightApproval);
